// One Mission Panel card's job and epic controls as state (#6753, epic #6552; prototype
// jobCtl `:2140`, allBtn `:2284`, epicCtl `:2339`, the handlers `:2623-2660`, the model
// change `:2702-2708`). The components under `components/machine-card/` render it; every
// write goes through the page's ONE flood guard (`./flood-guard.ts`, from context) and is
// followed by a re-read (`onWritten`).
//
// THE CARD FOLLOWS THE REAL JOB (owner ruling 2026-10-20). A job's Pause / Resume value is its
// row's state (`pausing` / `paused` read paused) until a change is waiting, then the waiting
// value (shown at once); after an answer, an OVERRIDE holds what the server said until the
// first fleet read that STARTED after the answer lands, and that read wins whatever it shows
// (`./override-bridge.ts`, shared with the epic overrides). Like the two § Boundary exceptions in
// `apps/dashboard/CLAUDE.md`, it paints from the answer and still re-reads: a 202
// `pause_requested` reads "Pausing…" until the row reads `paused`; an epic pause reads
// "Paused by you" from the answer's `paused_at` at once; a model pick shows "From the next
// slice" for 4 s. Recorded there as this slice's exception.
//
// STOP IS SENT LATE. A confirmed Stop shows "<name> job stopped." with Undo for 6 500 ms, and
// the cancel is sent only when that window ends (`STOP_UNDO_MS`, the guard's `outlivesLock`):
// a cancel is terminal and cannot be taken back. Undo sends nothing.

import { createEffect, createSignal, onCleanup, untrack } from 'solid-js';
import { t, tPlural } from '../i18n';
import { cancelTask } from './fleet';
import type { UtilBox, UtilJob } from './fleetUtilization';
import { STOP_UNDO_MS } from './flood-guard';
import { lockedSeconds, type PageGuard, wantedValue } from './flood-guard-context';
import {
  ANYWHERE_REASONS,
  type Door,
  pauseMachineJobs,
  pauseTask,
  readFailure,
  refusalText,
  resumeMachineJobs,
  resumeTask,
  type SkippedJob,
} from './job-controls';
import { boxJobs, capabilityKey, type JobTag, jobState, jobTag, jobTitle } from './machine-jobs';
import { type Bridged, bridge, settleBridges } from './override-bridge';

/** A job's pause value: running, or paused (pausing counts as paused: the request stands). */
export type PauseValue = 'run' | 'pause';

/** A refusal or failure shown under its job, epic or machine, translated. */
export type ControlRefusal = {
  readonly text: string;
  /** A resume a gone, offline or unreadable machine refused: offer "Resume on any machine". */
  readonly anywhere: boolean;
};

/** A job a batch door skipped, under the epic or the job it names. */
export type SkipLine = ControlRefusal & { readonly taskId: string; readonly name: string };

/** An open Stop confirm. */
export type StopAsk = { readonly kind: 'job' | 'epic'; readonly id: string; readonly name: string };

/** The note under a Stop: with Undo while the window runs, then the answer, without. */
export type StopNote = {
  readonly kind: 'job' | 'epic';
  readonly id: string;
  readonly text: string;
  readonly undo: boolean;
};

/** The guard's control keys: one per control, page-wide. */
export const controlKey = {
  jobPause: (id: string) => `job:${id}:pause`,
  jobStop: (id: string) => `job:${id}:stop`,
  jobAnywhere: (id: string) => `job:${id}:anywhere`,
  allJobs: (boxId: string) => `machine:${boxId}:jobs`,
  epicPause: (id: string) => `epic:${id}:pause`,
  epicStop: (id: string) => `epic:${id}:stop`,
  epicModel: (id: string) => `epic:${id}:model`,
};

/** Controls show on a job that is running, starting, pausing or paused (prototype `controllable`). */
export function controllableJob(job: Pick<UtilJob, 'state'>): boolean {
  const s = jobState(job);
  return s === 'working' || s === 'starting' || s === 'pausing' || s === 'paused';
}

/** The row's own pause value: `pausing` and `paused` read paused. */
export function rowPauseValue(job: Pick<UtilJob, 'state'>): PauseValue {
  const s = jobState(job);
  return s === 'pausing' || s === 'paused' ? 'pause' : 'run';
}

/**
 * What an answer said about a job until a newer read lands: a pause requested on a started
 * run (`pausing`), a job paused at once (`paused`, one its machine had not started), or a
 * resume or a withdrawn pause (`run`).
 */
export type JobOverride = 'pausing' | 'paused' | 'run';

/**
 * The tag a job shows under its controls (#6753): "Pausing…" from the moment Pause is
 * pressed until the row reads `paused` (then "Paused by you" or "Epic paused" from the row);
 * a job paused at once reads "Paused by you"; one being resumed loses its pause tag at once.
 * Otherwise the row's own tag.
 */
export function shownJobTag(
  job: UtilJob,
  value: PauseValue,
  override: JobOverride | undefined,
): JobTag | null {
  const row = rowPauseValue(job);
  if (value === 'pause' && row === 'run') return override === 'paused' ? 'paused' : 'pausing';
  if (value === 'run' && row === 'pause') return null;
  return jobTag(job);
}

/** "Coder": the capability a job's role reads as, the word every control names it by. */
export const jobName = (job: Pick<UtilJob, 'role'>): string => t(capabilityKey(job.role));

const withEntry = <K, V>(m: ReadonlyMap<K, V>, k: K, v: V): ReadonlyMap<K, V> =>
  new Map(m).set(k, v);
const without = <K, V>(m: ReadonlyMap<K, V>, k: K): ReadonlyMap<K, V> => {
  if (!m.has(k)) return m;
  const next = new Map(m);
  next.delete(k);
  return next;
};

export type CardControlsInput = {
  readonly page: PageGuard;
  readonly box: () => UtilBox;
  readonly token: string | null;
  /** Re-read the fleet: every write is followed by one, whatever it answered. */
  readonly onWritten: () => void;
  /**
   * When the fetch behind the read the card shows STARTED (`Date.now` ms; 0 before one). An
   * override is a bridge to the first read that started after its answer (`./override-bridge.ts`).
   */
  readonly readAt: () => number;
};

/** The card's shared control state: refusals, the Stop ask and note, the live region. */
function createControlState(input: CardControlsInput) {
  const { page } = input;
  const [jobOv, setJobOv] = createSignal<ReadonlyMap<string, Bridged<JobOverride>>>(new Map());
  const [refusals, setRefusals] = createSignal<ReadonlyMap<string, ControlRefusal>>(new Map());
  const [ask, setAsk] = createSignal<StopAsk | null>(null);
  // One note per pending Stop, keyed by kind and id (review thread 4436513176): a second
  // Stop inside the first one's Undo window must not take the first one's Undo away.
  const [notes, setNotes] = createSignal<ReadonlyMap<string, StopNote>>(new Map());
  const noteKey = (kind: StopNote['kind'], id: string): string => `${kind}:${id}`;
  const [live, setLive] = createSignal('');
  const [flash, setFlash] = createSignal(false);
  const timers = new Set<ReturnType<typeof setTimeout>>();
  const later = (ms: number, fn: () => void): void => {
    const h = setTimeout(() => {
      timers.delete(h);
      fn();
    }, ms);
    timers.add(h);
  };
  onCleanup(() => {
    for (const h of timers) clearTimeout(h);
    timers.clear();
  });
  // An override bridges to the first read that STARTED after its answer, and that read wins
  // whatever it shows (review thread 4436681010); a job gone from the card takes its own.
  createEffect(() => {
    const jobs = boxJobs(input.box());
    const readAt = input.readAt();
    untrack(() => {
      let next = settleBridges(jobOv(), readAt);
      for (const id of next.keys()) {
        if (!jobs.some((j) => j.id === id)) next = without(next, id);
      }
      if (next !== jobOv()) setJobOv(next);
    });
  });
  /** Hold what an answer said about a job until a newer read lands. */
  const overrideJob = (id: string, o: JobOverride): void => {
    setJobOv((m) => withEntry(m, id, bridge(o, Date.now())));
  };
  const refuse = (id: string, r: ControlRefusal): void => {
    setRefusals((m) => withEntry(m, id, r));
  };
  const clearRefusal = (id: string): void => {
    setRefusals((m) => without(m, id));
  };
  return {
    input,
    page,
    /** The override an answer left on this job, if a newer read has not replaced it. */
    jobOverride: (id: string): JobOverride | undefined => jobOv().get(id)?.value,
    overrideJob,
    ask,
    setAsk,
    /** The note under this job or epic, if any. */
    noteFor: (kind: StopNote['kind'], id: string): StopNote | undefined =>
      notes().get(noteKey(kind, id)),
    /** Show a note under its job or epic, replacing only that one's. */
    putNote(n: StopNote): void {
      setNotes((m) => withEntry(m, noteKey(n.kind, n.id), n));
    },
    /** Take away the note under this job or epic, when it still matches `which`. */
    clearNote(kind: StopNote['kind'], id: string, which: 'undo' | 'answer'): void {
      setNotes((m) => {
        const n = m.get(noteKey(kind, id));
        if (n === undefined || n.undo !== (which === 'undo')) return m;
        return without(m, noteKey(kind, id));
      });
    },
    live,
    setLive,
    flash,
    setFlash,
    later,
    refuse,
    clearRefusal,
    refusal: (id: string): ControlRefusal | undefined => refusals().get(id),
    locked: (): number => lockedSeconds(page),
    /** A door's non-2xx: lock, re-read, or the translated sentence under `id`. */
    failed(err: unknown, door: Door, id: string): void {
      const f = readFailure(err, door);
      if (f.kind === 'rate_limited') page.guard.lockFor(f.seconds);
      else if (f.kind === 'refused')
        refuse(id, { text: refusalText(f.code, t), anywhere: f.anywhere });
      else if (f.kind === 'failed')
        refuse(id, { text: t('fleet.machines.ctl_failed'), anywhere: false });
    },
    /** A job's title for a skipped line; a job no longer on the card reads its id's tail. */
    titleOf(taskId: string): string {
      const job = boxJobs(input.box()).find((j) => j.id === taskId);
      return job === undefined ? taskId.slice(-8) : jobTitle(job, t);
    },
  };
}

type ControlState = ReturnType<typeof createControlState>;

const storedJob = (st: ControlState, job: UtilJob): PauseValue => {
  const o = st.jobOverride(job.id);
  return o === undefined ? rowPauseValue(job) : o === 'run' ? 'run' : 'pause';
};

async function sendJob(st: ControlState, job: UtilJob, value: PauseValue): Promise<void> {
  const { input } = st;
  const name = jobName(job);
  try {
    if (value === 'pause') {
      const a = await pauseTask(job.id, input.token);
      // Every 2xx sets what the server reports, idempotent ones too (review thread
      // 4436600065): `already_paused` means a pause stands, whatever the last read says.
      st.overrideJob(job.id, a.kind === 'paused' ? 'paused' : 'pausing');
      st.setLive(t('fleet.machines.ctl_job_paused_live', { name }));
    } else {
      const a = await resumeTask(job.id, false, input.token);
      // `already_resumed` too: another client resumed it before this read caught up.
      st.overrideJob(job.id, 'run');
      st.setLive(
        a.kind === 'pause_withdrawn'
          ? t('fleet.machines.ctl_job_withdrawn_live', { name })
          : t('fleet.machines.ctl_job_resumed_live', { name }),
      );
    }
  } catch (err) {
    st.failed(err, value === 'pause' ? 'pause' : 'resume', job.id);
  } finally {
    input.onWritten();
  }
}

async function sendAnywhere(st: ControlState, taskId: string): Promise<void> {
  const { input } = st;
  const job = boxJobs(input.box()).find((j) => j.id === taskId);
  try {
    await resumeTask(taskId, true, input.token);
    st.overrideJob(taskId, 'run');
    st.clearRefusal(taskId);
    st.setLive(
      t('fleet.machines.ctl_job_resumed_live', {
        name: job === undefined ? st.titleOf(taskId) : jobName(job),
      }),
    );
  } catch (err) {
    st.failed(err, 'resume', taskId);
  } finally {
    input.onWritten();
  }
}

/** The skipped jobs of a batch answer, each as a refusal under its own job. */
function refuseSkipped(st: ControlState, skipped: readonly SkippedJob[], resume: boolean): void {
  for (const s of skipped) {
    st.refuse(s.task_id, {
      text: refusalText(s.reason, t),
      anywhere: resume && ANYWHERE_REASONS.has(s.reason),
    });
  }
}

async function sendAll(st: ControlState, value: PauseValue): Promise<void> {
  const { input } = st;
  const boxId = input.box().id;
  try {
    const a =
      value === 'pause'
        ? await pauseMachineJobs(boxId, input.token)
        : await resumeMachineJobs(boxId, input.token);
    const changed: readonly (readonly [string, JobOverride])[] =
      value === 'pause'
        ? [
            ...a.pause_requested.map((id) => [id, 'pausing'] as const),
            ...a.paused.map((id) => [id, 'paused'] as const),
          ]
        : [...a.resumed, ...a.pause_withdrawn].map((id) => [id, 'run'] as const);
    for (const [id, o] of changed) st.overrideJob(id, o);
    refuseSkipped(st, a.skipped, value === 'run');
    const n = a.skipped.length;
    if (value === 'pause') {
      st.setLive(
        n === 0
          ? t('fleet.machines.ctl_all_paused_live')
          : tPlural('fleet.machines.ctl_all_paused_skipped', n, { count: n }),
      );
    } else {
      st.setLive(
        n === 0
          ? t('fleet.machines.ctl_all_resumed_live')
          : tPlural('fleet.machines.ctl_all_resumed_skipped', n, { count: n }),
      );
    }
  } catch (err) {
    st.failed(err, 'jobs', boxId);
  } finally {
    input.onWritten();
  }
}

/** Pause, Resume and "Resume on any machine" on a job; Pause all / Resume all on the machine. */
function jobActions(st: ControlState) {
  const { page, input } = st;
  const jobValue = (job: UtilJob): PauseValue =>
    (wantedValue(page, controlKey.jobPause(job.id)) as PauseValue | undefined) ??
    storedJob(st, job);
  const controllable = (): readonly UtilJob[] => boxJobs(input.box()).filter(controllableJob);
  /** Pause all while any controllable job runs, else Resume all (prototype allBtn). */
  const allStored = (): PauseValue =>
    controllable().some((j) => storedJob(st, j) === 'run') ? 'run' : 'pause';
  const allValue = (): PauseValue =>
    (wantedValue(page, controlKey.allJobs(input.box().id)) as PauseValue | undefined) ??
    allStored();
  return {
    jobValue,
    /** The tag this job shows now ({@link shownJobTag}). */
    tagOf: (job: UtilJob): JobTag | null => shownJobTag(job, jobValue(job), st.jobOverride(job.id)),
    /** Pause, or (paused / pausing) Resume, on a job; Resume on a pausing job withdraws. */
    toggleJob(job: UtilJob): void {
      st.clearRefusal(job.id);
      page.guard.change<PauseValue>(controlKey.jobPause(job.id), {
        from: storedJob(st, job),
        to: jobValue(job) === 'run' ? 'pause' : 'run',
        send: (v) => sendJob(st, job, v),
      });
    },
    resumeAnywhere(taskId: string): void {
      page.guard.change(controlKey.jobAnywhere(taskId), {
        from: 'here',
        to: 'anywhere',
        send: () => sendAnywhere(st, taskId),
      });
    },
    /** Pause all / Resume all: shown with two or more controllable jobs. */
    allShown: (): boolean => controllable().length >= 2,
    allCount: (): number => controllable().length,
    allValue,
    toggleAll(): void {
      st.clearRefusal(input.box().id);
      page.guard.change<PauseValue>(controlKey.allJobs(input.box().id), {
        from: allStored(),
        to: allValue() === 'run' ? 'pause' : 'run',
        send: (v) => sendAll(st, v),
      });
    },
  };
}

/** Stop on a job or an epic: ask, then the note with Undo, then the late send. */
function stopActions(st: ControlState) {
  const { page, input } = st;
  /**
   * A confirmed Stop: the note with Undo now, the send when {@link STOP_UNDO_MS} ends. Undo
   * (or the page leaving) drops it and nothing is sent; a lock that starts meanwhile does not.
   */
  const queueStop = (
    a: StopAsk,
    key: string,
    text: { readonly stopped: string; readonly restored: string },
    send: () => Promise<void>,
  ): void => {
    const clear = (): void => st.clearNote(a.kind, a.id, 'undo');
    const verdict = page.guard.change(key, {
      from: 'keep',
      to: 'stop',
      delayMs: STOP_UNDO_MS,
      outlivesLock: true,
      send: async () => {
        clear();
        await send();
      },
      onDropped: (why) => {
        clear();
        if (why === 'cancelled') st.setLive(text.restored);
      },
    });
    if (verdict !== 'queued') return;
    st.putNote({ kind: a.kind, id: a.id, text: text.stopped, undo: true });
    st.setLive(text.stopped);
  };
  const takeAsk = (): StopAsk | null => {
    const a = st.ask();
    st.setAsk(null);
    return a;
  };
  return {
    queueStop,
    takeAsk,
    /** Open a Stop confirm: the card flashes red once (900 ms) and stays outlined until answered. */
    askStop(a: StopAsk): void {
      st.setAsk(a);
      st.setFlash(true);
      st.later(900, () => st.setFlash(false));
    },
    cancelAsk: (): void => {
      st.setAsk(null);
    },
    /** "Stop job": the note and Undo now, the cancel when the window ends. */
    confirmJobStop(job: UtilJob): void {
      const a = takeAsk();
      if (a === null) return;
      const name = jobName(job);
      queueStop(
        a,
        controlKey.jobStop(job.id),
        {
          stopped: t('fleet.machines.ctl_job_stopped', { name }),
          restored: t('fleet.machines.ctl_job_restored', { name }),
        },
        async () => {
          try {
            await cancelTask(job.id, input.token);
          } catch (err) {
            st.failed(err, 'task_cancel', job.id);
          } finally {
            input.onWritten();
          }
        },
      );
    },
    /** Undo this job's or epic's queued Stop, and only that one: nothing is sent. */
    undo(kind: StopNote['kind'], id: string): void {
      if (st.noteFor(kind, id)?.undo !== true) return;
      page.guard.cancel(kind === 'job' ? controlKey.jobStop(id) : controlKey.epicStop(id));
    },
  };
}

/** One card's controls. Made by the card, under the page's guard. */
export function createCardControls(input: CardControlsInput) {
  const st = createControlState(input);
  return { ...st, ...jobActions(st), ...stopActions(st) };
}

export type CardControls = ReturnType<typeof createCardControls>;
